import { useEffect, useState } from 'react';
import { Link, PLATFORM_LABEL, RUNNING, api, compact, countryName, fmtDate, navigate, nf, plural, targetsTitle, usd, useApi } from '../lib.jsx';
import { Modal, PlatformDot, Section, Status, Toast } from '../ui.jsx';
import { Card } from '../charts.jsx';
import { FORMAT_LABEL } from './LinkedIn.jsx';
import { ScopeSummary } from './NewResearch.jsx';
import { Report } from './Report.jsx';
import { drops, stepLabel } from '../../../server/pipeline/platforms.ts';

const STAGES = [
  ['planning', 'Planning'], ['discovering', 'Finding posts'], ['collecting_comments', 'Reading comments'],
  ['processing_context', 'Video context'], ['checking_evidence', 'Checking evidence'], ['analysing', 'Analysing'],
];
function stageState(stage, run) {
  if (run.status === stage) return 'active';
  const order = STAGES.map((s) => s[0]);
  if (order.indexOf(run.status) > order.indexOf(stage)) return 'done';
  if (RUNNING.includes(run.status)) return 'todo';
  // Finished: a stage is done only if a job for it actually ran.
  if (stage === 'planning') return 'done';
  if (stage === 'checking_evidence' || stage === 'analysing') return run.reports.length ? 'done' : 'todo';
  if (stage === 'processing_context') return run.jobs.some((j) => j.stage === 'comments') ? 'done' : 'todo';
  const step = stage === 'discovering' ? 'discovery' : 'comments';
  return run.jobs.some((j) => j.stage === step) ? 'done' : 'todo';
}

const dur = (ms) => { const s = Math.max(0, Math.round(ms / 1000)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m ${s % 60}s`; };

/** From the stored start and finish times, so a refresh never resets it. No time-remaining guess. */
function Elapsed({ run, running }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!running) return; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [running]);
  const from = Date.parse(run.createdAt);
  if (running) return <span aria-live="off">Working for {dur(now - from)}</span>;
  return run.finishedAt ? <span>Took {dur(Date.parse(run.finishedAt) - from)}</span> : null;
}

const credits = (x) => `${(x ?? 0).toFixed(2)} credits`;

const STEP = { queued: 'Waiting to start', context: 'Video context', classify: 'Labelling comments', report: 'Counting and writing' };
function TimingsTable({ timings, name }) {
  const rows = Object.entries(timings).map(([k, ms]) => {
    const [step, t, p] = k.split(':');
    return { k, ms, label: t ? `${step === 'discovery' ? 'Finding posts' : 'Reading comments'} · ${name(t)} · ${PLATFORM_LABEL[p] ?? p}` : STEP[k] ?? k };
  });
  if (!rows.length) return null;
  return (
    <details className="more">
      <summary>Stage timings (admins)</summary>
      <p className="hint">Platforms run side by side, so these overlap and add up to more than the total.</p>
      <div className="table-wrap"><table className="data">
        <thead><tr><th>Stage</th><th className="num">Took</th></tr></thead>
        <tbody>{rows.map((x) => <tr key={x.k}><td>{x.label}</td><td className="num">{dur(x.ms)}</td></tr>)}</tbody>
      </table></div>
    </details>
  );
}

// Where a run's data and pages live: the client app's, or the admin portal's read-only view of a client's research.
const CLIENT_PATHS = { api: (id) => `/api/runs/${id}`, page: (id) => `/runs/${id}`, print: (id) => `/reports/${id}/print`, back: '/reports', backLabel: '← Reports' };

/** `readOnly` (the admin portal): the same page without anything that starts, stops, changes or deletes. */
export function RunPage({ id, me, paths = CLIENT_PATHS, readOnly = false }) {
  const base = paths.api(id);
  const { data: run, error, reload } = useApi(base);
  const [toast, setToast] = useState(null);
  const [version, setVersion] = useState(null);
  const [dialog, setDialog] = useState(null); // 'analyse' | 'delete' | 'cancel' | 'platforms'
  const [reason, setReason] = useState('');
  const running = run && (RUNNING.includes(run.status) || run.analysing);
  const latest = run?.reports[0]?.version;

  useEffect(() => {
    if (!running) return;
    const t = setInterval(reload, 2000);
    return () => clearInterval(t);
  }, [running, reload]);

  function flash(t) { setToast(t); setTimeout(() => setToast(null), 4000); }
  async function cancel() {
    setDialog(null);
    try { const r = await api(`/api/runs/${id}/cancel`, { method: 'POST', body: {} }); flash(r.note); reload(); } catch (e) { flash(e.message); }
  }
  async function resume() {
    try { await api(`/api/runs/${id}/resume`, { method: 'POST', body: {} }); flash('Resumed.'); reload(); } catch (e) { flash(e.message); }
  }
  async function analyse() {
    setDialog(null);
    try { await api(`/api/runs/${id}/analyse`, { method: 'POST', body: { reason: reason || (latest ? 'Re-analysis' : 'First analysis') } }); setReason(''); flash('Analysing. The new version appears here when it is ready.'); reload(); } catch (e) { flash(e.message); }
  }
  async function remove() {
    setDialog(null);
    try { await api(`/api/runs/${id}`, { method: 'DELETE' }); navigate('/'); } catch (e) { flash(e.message); }
  }

  if (error) return <div className="page"><div className="error-box">{error.message}</div></div>;
  if (!run) return <div className="page"><p className="muted">Loading…</p></div>;
  const b = run.brief;
  const name = (tid) => b.targets.find((t) => t.id === tid)?.name ?? tid;
  // A scan collects posts and comments only: no model step, no report.
  const scan = b.preset === 'scan';
  const stages = scan ? STAGES.slice(0, 3) : STAGES;
  const copy = scan ? { ...STAGE_COPY, discovering: ['Finding posts…', 'Searching each platform; the posts are ranked by engagement, with no AI checks.'] } : STAGE_COPY;

  const hasComments = Object.values(run.counts ?? {}).some((c) => c.comments);
  const canAnalyse = !running && run.status !== 'cancelled' && hasComments && !scan;
  // A stopped run without a report can carry on, or be written up from what it already collected.
  const stopped = !running && run.status === 'cancelled' && !latest;
  const vq = version ? `?version=${version}` : '';
  const sums = Object.values(run.counts ?? {}).reduce((a, c) => ({ posts: a.posts + (c.selected ?? 0), comments: a.comments + (c.comments ?? 0), candidates: a.candidates + (c.candidates ?? 0) }), { posts: 0, comments: 0, candidates: 0 });
  // A post selected for two targets is one post: the totals count it once, and say when the per-target rows add up to more.
  const totals = { ...sums, ...(run.unique ?? {}) };
  // Jobs that failed, stopped early, came back empty or hit a limit, worst first, each with why.
  const problems = (run.jobs ?? []).filter((j) => j.problem).sort((x, y) => KINDS.indexOf(x.problem.kind) - KINDS.indexOf(y.problem.kind));
  // Platforms that gave the report nothing, flagged on the button that opens the table. "Partial" is left to the table:
  // a busy post with more comments than the plan reads makes most platforms partial, so flagging it would always show.
  const empty = (run.platforms ?? []).filter((x) => x.kind === 'neg').length;
  const shared = sums.posts > totals.posts ? ` (${nf.format(sums.posts)} across targets: some posts are about more than one)` : '';

  return (
    <div className="page">
      <div className="run-head">
        <Link to={paths.back} className="back small">{paths.backLabel}</Link>
        <h1>{targetsTitle(b.targets.map((t) => t.name))}</h1>
        <p className="run-question">{b.question}</p>
        <div className="meta">
          {latest && !running ? <span className={`badge ready${run.status === 'partial' ? ' partial' : ''}`} title={run.status === 'partial' ? 'Some collection jobs failed or stopped early: see Platforms for what each one gave' : undefined}>{run.status === 'partial' ? 'Report ready · partial collection' : 'Report ready'}</span> : <Status status={run.status} />}
          {b.platforms.map((p) => <PlatformDot key={p} p={p} />)}
          <span>{fmtDate(b.since)} – {fmtDate(b.until)}</span>
          <span>{countryName(b.market)}</span>
          <span className="cap-first">{b.preset}</span>
          <Elapsed run={run} running={running} />
          {run.credits && !running ? <span>Used {credits(run.credits.used)}</span> : null}
          {run.supplier && run.supplier !== 'apify' ? <span className="tag" title="Admins only">{run.supplier === 'brightdata' ? 'Bright Data test run' : run.supplier}</span> : null}
          {run.cost ? <span title="Admins only">Cost {usd(run.cost.actual, 3)}{run.cost.pendingBilling ? ' (billing pending)' : ''}</span> : null}
        </div>
        <div className="run-actions">
          {run.reports.length > 1 ? (
            <label className="version-pick small">Version
              <select value={version ?? latest} onChange={(e) => setVersion(Number(e.target.value) === latest ? null : Number(e.target.value))}>
                {run.reports.map((v) => <option key={v.version} value={v.version}>v{v.version}{v.version === latest ? ' (latest)' : ''} · {fmtDate(v.createdAt, { day: 'numeric', month: 'short' })}{v.reason ? ` · ${v.reason.slice(0, 40)}` : ''}</option>)}
              </select>
            </label>
          ) : null}
          {latest && run.platforms ? (
            <button type="button" className="btn small" onClick={() => setDialog('platforms')} title="What each platform gave this report, and why">
              <span className="info-icon" aria-hidden="true">i</span>Platforms{empty ? <span className="muted"> · {empty} gave nothing</span> : null}
            </button>
          ) : null}
          {latest ? (
            <details className="menu">
              <summary className="btn small">Export</summary>
              <div className="menu-list">
                <a href={`${paths.print(id)}${vq}`} target="_blank" rel="noopener">Print or save as PDF<span>The report with its evidence references</span></a>
                <a href={`${base}/export.xlsx${vq}`}>Excel workbook<span>Every chart and table as native Excel charts, with the comments and cited evidence</span></a>
                <a href={`${base}/export.csv${vq}`}>CSV: comments in the report<span>The comments the report counts, with their labels; opens in Excel</span></a>
                <a href={`${base}/export.csv?scope=all${version ? `&version=${version}` : ''}`}>CSV: every collected comment<span>All comments under the selected posts, including ones about something else</span></a>
                {me.internal ? <a href={`${base}/export.csv?scope=candidates${version ? `&version=${version}` : ''}`}>CSV: every post found (admins)<span>Kept or left out, with the reason for each</span></a> : null}
              </div>
            </details>
          ) : null}
          {readOnly ? null : <>
            {canAnalyse ? <button className="btn small" onClick={() => setDialog('analyse')}>{latest ? 'Analyse again' : 'Analyse'}</button> : null}
            {running && !run.cancelRequested ? <button className="btn small ghost danger" aria-label="Stop research" onClick={() => setDialog('cancel')}><span className="stop-icon" aria-hidden="true" />Stop</button> : null}
            {stopped ? <button className="btn small" onClick={resume}><span className="play-icon" aria-hidden="true" />Resume</button> : null}
            {stopped && hasComments && !scan ? <button className="btn small" onClick={() => setDialog('analyse')}>Write report from what was collected</button> : null}
            {!running ? <button className="btn small ghost danger" onClick={() => setDialog('delete')}>Delete</button> : null}
          </>}
          {run.cancelRequested && running ? <span className="small muted">Stopping. Work that already started may still be billed.</span> : null}
        </div>
      </div>
      {stopped && !readOnly ? <div className="warn-box" style={{ marginBottom: 20 }}>Stopped: someone pressed Stop. Resume to carry on collecting (jobs that finished are not bought again){scan ? '' : `, write a report from the ${nf.format(totals.comments)} comments already collected`}, or delete it.</div> : null}
      {run.synthetic ? <div className="demo-banner">This research used demo data. The posts and comments are synthetic and only show how the product works.</div> : null}
      {run.error || run.status === 'partial' || problems.some((j) => !['limit', 'note'].includes(j.problem.kind)) ? (
        <div className={run.status === 'failed' ? 'error-box' : 'warn-box'} style={{ marginBottom: 20 }}>
          {run.error ? run.error.replace(' See the log.', '') : run.status === 'partial' ? 'Some collection jobs failed or stopped early. Everything that was collected is kept and used.' : problems.some((j) => ['failed', 'stopped'].includes(j.problem.kind)) ? 'Some collection jobs did not finish.' : 'Some searches found nothing.'}
          {problems.length && !(latest && run.funnel) ? <ul className="problems">{problems.filter((j) => !['limit', 'note'].includes(j.problem.kind)).map((j, i) => <li key={i}><b>{PLATFORM_LABEL[j.platform]} · {stepLabel(j)}{b.targets.length > 1 ? ` (${name(j.targetId)})` : ''}:</b> {j.problem.text}</li>)}</ul> : null}
        </div>
      ) : null}
      {run.analysisError && !run.analysing ? <div className="warn-box" style={{ marginBottom: 20 }}>No report yet: {run.analysisError}</div> : null}

      {running ? (
        <section className="live-progress" aria-live="polite">
          <div className="lp-head">
            <span className="spinner" aria-hidden="true" />
            <div>
              <h2>{run.analysing && latest ? 'Making a new report version…' : copy[run.analysing ? 'analysing' : run.status]?.[0] ?? 'Working…'}</h2>
              <p className="muted">{run.analysing && latest ? 'The current version stays readable below until the new one is saved.' : copy[run.status]?.[1]}</p>
            </div>
          </div>
          <ol className="stages">{stages.map(([k, label]) => <li key={k} data-state={stageState(k, run)}>{label}</li>)}</ol>
          {run.credits ? <div className="credits"><p><strong>{credits(run.credits.used)}</strong> used so far</p></div> : null}
          <div className="lp-counts">
            <span><b>{nf.format(totals.candidates)}</b> posts found</span>
            <span><b>{nf.format(totals.posts)}</b> selected</span>
            <span><b>{nf.format(totals.comments)}</b> comments read</span>
          </div>
          <p className="small muted">You can leave this page; the research keeps running and the report will be here when you come back.</p>
        </section>
      ) : null}

      {latest ? <Report key={version ?? latest} base={base} print={paths.print(id)} version={version} admin={me.internal} readOnly={readOnly} /> : null}
      {scan && !running ? <ScanPosts base={base} /> : null}

      <h2 className="behind eyebrow">{latest ? 'Behind this report' : 'What was collected'}</h2>
      <Section title="Sources" hint={`${nf.format(totals.posts)} posts and ${nf.format(totals.comments)} comments${shared}`} open={!latest && !running && !scan}>
        <SourcesTable counts={run.counts} plan={run.plan} name={b.targets.length > 1 ? name : null} />
      </Section>
      {run.plan ? <Section title="What was searched" hint="the searches, and what they can't find" open={false}><ScopeSummary plan={run.plan} /></Section> : null}
      {!running ? <Section title="Posts and comments" hint="everything collected, unfiltered" open={!latest && !scan}><Evidence base={base} brief={b} name={name} admin={me.internal} readOnly={readOnly} /></Section> : null}
      {me.internal ? (
        <Section title="Technical details" hint={`Yapscape team only · ${nf.format(totals.candidates)} posts found, checks, jobs and the activity log`} open={false}>
          {!running ? <ol className="stages">{stages.map(([k, label]) => <li key={k} data-state={stageState(k, run)}>{label}</li>)}</ol> : null}
          <CountsTable counts={run.counts} plan={run.plan} name={name} />
          {run.pair ? <PairTable id={id} paths={paths} /> : null}
          {run.funnel ? <FunnelTable rows={run.funnel} name={name} /> : null}
          {run.timings ? <TimingsTable timings={run.timings} name={name} /> : null}
          <JobsTable jobs={run.jobs} name={name} admin />
          <details className="more"><summary>Activity log</summary><pre className="log">{run.log.join('\n')}</pre></details>
        </Section>
      ) : null}

      {dialog === 'analyse' ? (
        <Modal title={latest ? 'Analyse again' : 'Analyse the collected comments'} onClose={() => setDialog(null)}
          actions={<><button className="btn ghost" onClick={() => setDialog(null)}>Cancel</button><button className="btn primary" onClick={analyse}>Start analysis</button></>}>
          <p>{latest ? 'This makes a new report version from the comments already collected. Nothing new is collected, labels for unchanged comments are reused, and earlier versions stay available.' : 'This labels the collected comments and writes the report. Nothing new is collected.'}</p>
          {latest ? <label className="field">Why? <span className="hint">saved with the version</span><input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. after correcting labels" autoFocus /></label> : null}
        </Modal>
      ) : null}
      {dialog === 'platforms' ? (
        <Modal wide title="What each platform gave this report" onClose={() => setDialog(null)}
          actions={<button className="btn" onClick={() => setDialog(null)}>Close</button>}>
          <PlatformStatus rows={run.platforms} />
        </Modal>
      ) : null}
      {dialog === 'delete' ? (
        <Modal title="Delete this research?" onClose={() => setDialog(null)}
          actions={<><button className="btn ghost" onClick={() => setDialog(null)}>Keep it</button><button className="btn primary danger-fill" onClick={remove}>Delete</button></>}>
          <p>The brief, everything collected, every report version and its feedback are deleted. This can't be undone.</p>
        </Modal>
      ) : null}
      {dialog === 'cancel' ? (
        <Modal title="Stop this research?" onClose={() => setDialog(null)}
          actions={<><button className="btn ghost" onClick={() => setDialog(null)}>Keep going</button><button className="btn primary danger-fill" onClick={cancel}>Stop</button></>}>
          <p>No new work will start. Work that already started may still use credits. Whatever was collected so far is kept.</p>
        </Modal>
      ) : null}
      <Toast text={toast} />
    </div>
  );
}

const STAGE_COPY = {
  queued: ['Getting ready…', 'Your research is in the queue.'],
  planning: ['Planning the searches…', 'Turning your brief into searches for each platform.'],
  discovering: ['Finding posts…', 'Searching each platform, then checking each post is really about what you asked.'],
  collecting_comments: ['Reading comments…', 'Collecting the comments and replies under the selected posts.'],
  processing_context: ['Reading subtitles, speech and video frames…', 'Each selected video’s own subtitles or its transcribed speech, and frames sampled from it for on-screen text and what is shown, help interpret its comments.'],
  checking_evidence: ['Labelling comments…', 'Each comment is labelled for what it is about, sentiment and themes, using its post as context.'],
  analysing: ['Writing the report…', 'Counting themes and sentiment, then writing findings that cite the comments behind them.'],
};

/** A scan's result (server/scan.ts): each platform's posts, most engagement first, with their most-liked comments. */
function ScanPosts({ base }) {
  const { data: v, error } = useApi(`${base}/scan`);
  if (error) return <div className="error-box">{error.message}</div>;
  if (!v) return <p className="muted">Loading the posts…</p>;
  return (
    <div className="dgrid">{v.platforms.map((p) => {
      const left = [p.rejected ? `${nf.format(p.rejected)} outside the dates, job adverts or exclusion words` : '', p.elsewhere ? `${nf.format(p.elsewhere)} by authors in other countries` : ''].filter(Boolean).join('; ');
      const likes = p.platform === 'linkedin' ? 'reaction' : 'like';
      return (
        <Card key={p.platform} span={12} title={`${PLATFORM_LABEL[p.platform] ?? p.platform}: ${plural(p.kept, 'post')}`} note={p.posts.length ? null : 'No posts found.'}
          denom={`Most engagement first (${likes}s, comments and shares at collection)${p.kept > p.posts.length ? `; the top ${p.posts.length} shown` : ''}. No AI has checked them: “No topic words” marks a post that doesn’t use the topic’s words.${left ? ` Left out: ${left}.` : ''}`}>
          <ol className="space-posts">{p.posts.map((x) => (
            <li key={x.id}>
              <div className="small"><b>{x.author ?? 'Unknown'}</b>{x.followers !== null ? ` · ${compact(x.followers)} followers` : ''}{x.country ? ` · ${countryName(x.country)}` : ''}{x.format ? ` · ${FORMAT_LABEL[x.format] ?? x.format}` : ''}{x.date ? ` · ${fmtDate(x.date)}` : ''}{x.mentionsTopic ? null : <> <span className="tag">No topic words</span></>}</div>
              {x.about ? <div className="small muted">{x.about.slice(0, 110)}</div> : null}
              <p className="space-text">{x.text}{x.text.length >= 400 ? '…' : ''}</p>
              <div className="small muted">{plural(x.reactions ?? 0, likes)} · {plural(x.comments ?? 0, 'comment')}{x.shares ? ` · ${plural(x.shares, 'share')}` : ''}{x.views ? ` · ${compact(x.views)} views` : ''}{x.url ? <> · <a href={x.url} target="_blank" rel="noreferrer noopener">Open post</a></> : null}</div>
              {x.topComments.length ? (
                <details className="scan-comments">
                  <summary className="small">Most-liked comments ({nf.format(x.commentsRead)} read)</summary>
                  <ul>{x.topComments.map((c, i) => <li key={i} className="small"><b>{c.author ?? 'Someone'}</b>{c.likes ? ` (${plural(c.likes, 'like')})` : ''}: {c.text}</li>)}</ul>
                </details>
              ) : null}
            </li>
          ))}</ol>
        </Card>
      );
    })}</div>
  );
}

/** What was collected on each platform: the base the report stands on, without the checks in between. */
function SourcesTable({ counts, plan, name }) {
  const rows = plan?.entries ?? [];
  if (!rows.length) return null;
  return (
    <div className="table-wrap"><table className="data">
      <thead><tr>{name ? <th>Researching</th> : null}<th>Platform</th><th className="num">Posts</th><th className="num">Comments</th></tr></thead>
      <tbody>{rows.map((e) => {
        const c = counts[`${e.targetId}:${e.platform}`] ?? {};
        return (
          <tr key={e.targetId + e.platform}>
            {name ? <td>{name(e.targetId)}</td> : null}<td><PlatformDot p={e.platform} /></td>
            <td className="num">{nf.format(c.selected ?? 0)}</td><td className="num">{nf.format(c.comments ?? 0)}</td>
          </tr>
        );
      })}</tbody>
    </table></div>
  );
}

function CountsTable({ counts, plan, name }) {
  const rows = plan?.entries ?? [];
  if (!rows.length) return null;
  return (
    <div className="table-wrap"><table className="data">
      <thead><tr><th>Researching</th><th>Platform</th><th className="num">Candidates</th><th className="num" title="Names the target (or another name for it, never a related topic) in its caption, hashtags or account, or the model read it as about the target">Relevant</th><th className="num" title="Found by a search but names no target name; the model could not decide, or was not asked">Uncertain</th><th className="num" title="Outside the dates, another language, an exclusion word, or the model read it as not about the target">Rejected</th><th className="num">Posts selected</th><th className="num">Comments</th><th className="num">of which replies</th><th className="num" title="Near-identical text from different authors: kept and counted, flagged">Suspected copies</th><th className="num" title="Returned but not stored: media-only comments, over the cap, or not linkable to a post">Not stored</th></tr></thead>
      <tbody>{rows.map((e) => {
        const c = counts[`${e.targetId}:${e.platform}`] ?? {};
        const v = (k) => (c[k] === undefined ? '–' : nf.format(c[k]));
        return (
          <tr key={e.targetId + e.platform}>
            <td>{name(e.targetId)}</td><td>{PLATFORM_LABEL[e.platform]}</td>
            <td className="num">{v('candidates')}</td><td className="num">{v('relevant')}</td><td className="num">{v('uncertain')}</td><td className="num">{v('rejected')}</td>
            <td className="num">{v('selected')} <span className="muted small">/ {e.limits.posts}</span></td>
            <td className="num">{v('comments')} <span className="muted small">/ {nf.format(e.limits.comments)}</span></td>
            <td className="num">{v('replies')}</td><td className="num">{v('suspectedCopies')}</td><td className="num">{v('notStored')}</td>
          </tr>
        );
      })}</tbody>
    </table></div>
  );
}


const SUPPLIER = { apify: 'Apify', brightdata: 'Bright Data' };
const share = (x) => (x === null || x === undefined ? '–' : `${Math.round(x * 100)}%`);

/** Admins: the same brief on both suppliers, side by side. Every figure comes from stored records (GET …/compare). */
function PairTable({ id, paths }) {
  const { data: c } = useApi(`${paths.api(id)}/compare`);
  if (!c) return null;
  const [a, b] = c.runs;
  const row = (label, f, hint) => <tr key={label}><td>{label}{hint ? <span className="hint"> {hint}</span> : null}</td>{c.runs.map((r) => <td className="num" key={r.id}>{f(r)}</td>)}</tr>;
  return (
    <div className="pair">
      <h3 className="small">Supplier comparison <span className="muted">(Yapscape team only; the same brief, platforms and caps)</span></h3>
      <div className="table-wrap"><table className="data">
        <thead><tr><th>All platforms</th>{c.runs.map((r) => <th className="num" key={r.id}>{r.id === id ? SUPPLIER[r.supplier] : <Link to={paths.page(r.id)}>{SUPPLIER[r.supplier]}</Link>}</th>)}</tr></thead>
        <tbody>
          {row('Status', (r) => `${r.status}${r.report ? '' : ', no report yet'}`)}
          {row('Rows returned', (r) => nf.format(r.totals.discovered ?? 0))}
          {row('Unique posts', (r) => nf.format(r.totals.unique ?? 0))}
          {row('Relevant posts', (r) => `${nf.format(r.totals.relevant ?? 0)} (${share(r.rates.relevant)})`, 'share of unique posts')}
          {row('Posts bought for comments', (r) => nf.format(r.totals.selected ?? 0))}
          {row('Comments collected', (r) => nf.format(r.totals.comments ?? 0))}
          {row('Counted after both checks', (r) => `${nf.format(r.totals.included ?? 0)} (${share(r.rates.counted)})`, 'share of collected')}
          {row('Collection cost', (r) => `${usd(r.cost.collection, 3)}${r.cost.pending ? '*' : ''}`)}
          {row('Counted comments per $1 of collection', (r) => (r.rates.countedPerDollar === null ? '–' : nf.format(Math.round(r.rates.countedPerDollar))))}
          {row('Model cost', (r) => usd(r.cost.model, 3))}
          {row('Time to finish', (r) => (r.minutes === null ? 'running' : `${r.minutes} min`))}
        </tbody>
      </table></div>
      <div className="table-wrap"><table className="data">
        <thead><tr><th>Per platform</th>{c.runs.map((r) => <th className="num" key={r.id}>{SUPPLIER[r.supplier]}: relevant / unique</th>)}{c.runs.map((r) => <th className="num" key={r.id}>{SUPPLIER[r.supplier]}: counted / collected</th>)}<th className="num">Posts found by both</th></tr></thead>
        <tbody>{c.platforms.map(({ platform, foundByBoth }) => {
          const of = (r) => r.platforms.find((x) => x.platform === platform) ?? { unique: 0, relevant: 0, comments: 0, included: 0 };
          return <tr key={platform}><td><PlatformDot p={platform} /> {PLATFORM_LABEL[platform] ?? platform}</td>
            {c.runs.map((r) => <td className="num" key={r.id}>{nf.format(of(r).relevant)} / {nf.format(of(r).unique)}</td>)}
            {c.runs.map((r) => <td className="num" key={r.id}>{nf.format(of(r).included)} / {nf.format(of(r).comments)}</td>)}
            <td className="num">{nf.format(foundByBoth)}</td></tr>;
        })}</tbody>
      </table></div>
      {a && b && (a.cost.pending || b.cost.pending) ? <p className="hint">* billing pending: costs settle shortly after a job ends.</p> : null}
    </div>
  );
}

/**
 * Per platform, what it gave the report: complete, partial (a job failed or stopped early) or nothing, and the first stage
 * where it ran dry, so "0 comments from Instagram" reads as a collection result, never as an audience signal.
 */
function PlatformStatus({ rows }) {
  return (
    <section className="platform-status">
      <div className="table-wrap"><table className="data">
        <thead><tr><th>Platform</th><th>Collection</th><th className="num">Posts found</th><th className="num">About the target</th><th className="num">Comments</th><th className="num">Counted</th><th>Why</th></tr></thead>
        <tbody>{rows.map((x) => (
          <tr key={x.platform}>
            <td><PlatformDot p={x.platform} /></td><td><span className={`tag ${x.kind}`}>{x.label}</span></td>
            <td className="num" data-label="Posts found">{nf.format(x.found)}</td><td className="num" data-label="About the target">{nf.format(x.about)}</td>
            <td className="num" data-label="Comments">{nf.format(x.comments)}{x.onPlatform ? <span className="small muted"> of {nf.format(x.onPlatform)}</span> : null}</td><td className="num" data-label="Counted">{nf.format(x.counted)}</td>
            <td className="small why">{x.text || '–'}</td>
          </tr>
        ))}</tbody>
      </table></div>
      <p className="hint">Comments: collected, of how many the selected posts show on the platform. Counted: comments about the target that answer the research question, the ones the report rests on. A platform with no data says nothing about how people there feel.</p>
    </section>
  );
}

function FunnelTable({ rows, name }) {
  return (
    <details className="more">
      <summary>Funnel (admins)</summary>
      <p className="hint">Rows the searches returned → unique posts → checks → selected → comments → attributed → included in the report. A post or comment that drops out is counted once, at the first stage it fails.</p>
      <div className="table-wrap"><table className="data">
        <thead><tr><th>Researching</th><th>Platform</th><th className="num">Rows returned</th><th className="num">Unique posts</th><th className="num">Relevant</th><th className="num">Selected</th><th>Posts left out</th><th className="num">Comments</th><th className="num">About a target</th><th className="num">Included</th><th>Comments left out</th></tr></thead>
        <tbody>{rows.map((f) => (
          <tr key={f.targetId + f.platform}>
            <td>{name(f.targetId)}</td><td>{PLATFORM_LABEL[f.platform]}</td>
            <td className="num">{nf.format(f.discovered)}</td><td className="num">{nf.format(f.unique)}</td><td className="num">{nf.format(f.relevant)}</td><td className="num">{nf.format(f.selected)}{f.conflicts ? <div className="small" title="Selected, but the video frames do not show the target and nothing names it">{nf.format(f.conflicts)} in conflict</div> : null}</td>
            <td className="small">{drops(f.postsDropped)}</td>
            <td className="num">{nf.format(f.comments)}</td><td className="num">{nf.format(f.attributed)}</td>
            <td className="num">{nf.format(f.included)}{f.includedFromOther ? <span className="muted small" title="Comments collected for another target that count for this one too"> +{nf.format(f.includedFromOther)}</span> : null}</td>
            <td className="small">{drops(f.commentsDropped)}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </details>
  );
}

const KINDS = ['failed', 'stopped', 'empty', 'limit', 'note'];

function JobsTable({ jobs, name, admin }) {
  if (!jobs.length) return null;
  return (
    <details className="more">
      <summary>Collection jobs ({jobs.length})</summary>
      <div className="table-wrap"><table className="data">
        <thead><tr><th>Researching</th><th>Platform</th><th>Step</th><th>Status</th><th className="num">Records</th>{admin ? <><th>Supplier</th><th className="num">Reserved</th><th className="num">Actual</th></> : null}</tr></thead>
        <tbody>{jobs.map((j, i) => (
          <tr key={i}>
            <td>{name(j.targetId)}</td><td>{PLATFORM_LABEL[j.platform]}</td><td>{stepLabel(j)}</td>
            <td>{j.status}{j.capped ? <span className="tag">capped</span> : null}{j.empty ? <span className="tag">empty</span> : null}
              {j.error ? <div className="small" style={{ color: 'var(--neg)' }}>{j.error}</div> : null}
              {j.warnings.map((w, k) => <div key={k} className="small muted">{w}</div>)}</td>
            <td className="num">{nf.format(j.items)}</td>
            {admin ? <><td className="small">{j.supplier}{j.actor && j.actor !== j.supplier ? ` · ${j.actor}` : ''}</td><td className="num">{usd(j.reservedUsd)}</td><td className="num">{usd(j.actualUsd, 3)}{j.billing === 'pending' ? '*' : ''}</td></> : null}
          </tr>
        ))}</tbody>
      </table></div>
      {admin ? <p className="hint">* billing pending: the supplier settles costs about a minute after a job ends.</p> : null}
    </details>
  );
}

function Evidence({ base, brief, name, admin, readOnly }) {
  const [target, setTarget] = useState('');
  const [unselected, setUnselected] = useState(false);
  const [platform, setPlatform] = useState('');
  const [page, setPage] = useState(1);
  const q = new URLSearchParams({ page: String(page), ...(target ? { target } : {}), ...(platform ? { platform } : {}), ...(unselected ? { unselected: '1' } : {}) });
  const { data } = useApi(`${base}/posts?${q}`, [q.toString()]);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  return (
    <section>
      <p className="sub">{unselected ? 'Posts found but not selected, with the reason for each. Nothing was collected under them.' : 'The selected posts and every comment collected on them, unfiltered.'}</p>
      <div className="filters">
        {brief.targets.length > 1 ? (
          <select value={target} onChange={(e) => { setTarget(e.target.value); setPage(1); }} aria-label="Researching">
            <option value="">All</option>
            {brief.targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        ) : null}
        <select value={platform} onChange={(e) => { setPlatform(e.target.value); setPage(1); }} aria-label="Platform">
          <option value="">All platforms</option>
          {brief.platforms.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
        </select>
        {admin ? <label className="small"><input type="checkbox" checked={unselected} onChange={(e) => { setUnselected(e.target.checked); setPage(1); }} /> Found but not selected (admins)</label> : null}
        {data ? <span className="small muted">{nf.format(data.total)} posts</span> : null}
      </div>
      {!data ? <p className="muted">Loading…</p> : data.posts.map((p) => <PostCard key={p.id} post={p} name={name} base={readOnly ? null : base} admin={admin} />)}
      {pages > 1 ? (
        <div className="pager">
          <button className="btn small" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="small">Page {page} of {pages}</span>
          <button className="btn small" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      ) : null}
    </section>
  );
}

export function Thumb({ src, label }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <div className="thumb fallback" aria-hidden="true">{label}</div>;
  return <img className="thumb" src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
}

/** Platforms whose post text is a title, a blank line, then the description: the title alone is the preview. */
const TITLED = new Set(['youtube', 'reddit']);

/** A post's text as a short preview first (a YouTube video's or Reddit thread's title); the rest on request. */
export function Caption({ text, small, platform }) {
  const [all, setAll] = useState(false);
  if (!text) return <p className={`caption${small ? ' small' : ''}`}><span className="muted">No caption</span></p>;
  const cut = TITLED.has(platform) && text.indexOf('\n\n') > 0 ? text.indexOf('\n\n') : text.length > 240 ? 220 : -1;
  const long = cut > 0 && cut < text.length;
  return (
    <p className={`caption${small ? ' small' : ''}`}>{long && !all ? `${text.slice(0, cut).trimEnd()}${TITLED.has(platform) ? '' : '…'}` : text}
      {long ? <> <button type="button" className="linklike small" onClick={() => setAll(!all)}>{all ? 'Show less' : 'Read description'}</button></> : null}</p>
  );
}

/** What was collected against what the platform reports: a sample of the comments, never all of them. */
export const collectedOf = (n, onPlatform, amazon) => `${nf.format(n)} collected${onPlatform != null ? ` / ${compact(onPlatform)} ${amazon ? 'reviews' : 'comments'} on the platform` : ''}`;

function PostCard({ post, name, base, admin }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="post-card">
      <div className="post-head">
        <Thumb src={post.thumbnail_url} label={PLATFORM_LABEL[post.platform]} />
        <div className="post-main">
          <div className="qmeta">
            <PlatformDot p={post.platform} />
            <span>@{post.creator_handle ?? 'unknown'}</span>
            <span>{fmtDate(post.published_at)}</span>
            {post.likes !== null ? <span>{compact(post.likes)} likes</span> : null}
            {post.comment_count !== null ? <span>{compact(post.comment_count)} {post.platform === 'amazon' ? 'reviews on Amazon' : 'comments on platform'}</span> : null}
            {post.url ? <a href={post.url} target="_blank" rel="noreferrer noopener">Open on {PLATFORM_LABEL[post.platform]}</a> : null}
          </div>
          <Caption text={post.caption} platform={post.platform} />
          {admin ? (
            <div className="qmeta small">
              {Object.entries(post.screening).map(([tid, s]) => <span key={tid} className={`tag ${s.decision === 'rejected' ? 'neg' : ''}`} title={s.reason}>{name(tid)}: {s.decision}</span>)}
              <span className="muted">{post.selection_method}</span>
            </div>
          ) : null}
          {post.foundBy ? (
            <div className="small muted">
              {Object.entries(post.screening).map(([tid, s]) => <div key={tid}>{name(tid)}: {s.reason}{s.conflict ? <b> · conflict after video context: {s.conflict}</b> : null}</div>)}
              {post.foundBy.map((j, i) => <div key={i}>Found by {name(j.target)} {j.stage.replace('_', ' ')} ({j.actor}): {j.searches.join(' · ')}</div>)}
            </div>
          ) : null}
        </div>
      </div>
      {post.commentTotal ? (
        <>
          <button type="button" className="linklike small" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide comments' : 'Show collected comments'} ({collectedOf(post.commentTotal, post.comment_count, post.platform === 'amazon')})</button>
          {open ? <ul className="bubbles">{post.comments.map((c) => <Bubble key={c.id} c={c} postUrl={post.url} base={base} />)}</ul> : null}
        </>
      ) : <p className="small muted">No comments collected for this post{post.comment_count ? ` (${compact(post.comment_count)} on the platform)` : ''}.</p>}
    </article>
  );
}

/**
 * `extra(c)` adds labels (the report drawer); `c.match === false` marks thread context shown around a match and
 * `c.match === true` a comment the number counts. `more` follows its replies (the drawer's folded, uncounted ones).
 * `base` (a run's API path) offers "View in English": a machine translation made on request, the original kept.
 */
export function Bubble({ c, postUrl, reply, extra, base, more }) {
  const link = c.url ?? postUrl; // direct comment link when the platform gives one, otherwise the post
  const [en, setEn] = useState(c.translation ?? null);
  const [showEn, setShowEn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function english() {
    if (en) { setShowEn(true); return; }
    setBusy(true); setErr(null);
    try { const r = await api(`${base}/comments/${c.id}/translate`, { method: 'POST', body: {} }); setEn(r.translation); setShowEn(true); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <li className={`bubble${reply ? ' reply' : ''}${c.match === false ? ' context' : c.match ? ' counted' : ''}`}>
      <div className="qmeta small">
        <span>@{c.author ?? 'unknown'}</span><span>{fmtDate(c.published_at)}</span>
        {c.rating != null ? <span className="stars" title={`${c.rating} of 5 stars`}>{'★'.repeat(Math.round(c.rating))}{'☆'.repeat(5 - Math.round(c.rating))} {c.rating} of 5</span> : null}
        {c.likes !== null ? <span>{c.rating != null ? `${compact(c.likes)} found helpful` : `${compact(c.likes)} ${c.likes === 1 ? 'like' : 'likes'}`}</span> : null}
        {c.observations > 1 ? <span title="The supplier returned this comment more than once; it is counted once.">seen {c.observations}×</span> : null}
        {link ? <a href={link} target="_blank" rel="noreferrer noopener">{c.url ? 'Open comment' : 'Open post'}</a> : null}
      </div>
      {showEn ? <><p lang="en">{en}</p><p className="small muted">Machine translation, for reading only · <button type="button" className="linklike small" onClick={() => setShowEn(false)}>View original</button></p></>
        : <><p>{c.text}</p>{base ? <button type="button" className="linklike small" disabled={busy} onClick={english}>{busy ? 'Translating…' : 'View in English'}</button> : null}{err ? <span className="small muted"> {err}</span> : null}</>}
      {extra && c.match !== false ? extra(c) : null}
      {c.replies?.length || more ? <ul className="bubbles">{(c.replies ?? []).map((r) => <Bubble key={r.id} c={r} postUrl={postUrl} reply extra={extra} base={base} />)}{more}</ul> : null}
    </li>
  );
}
